import { useEffect } from 'react';

import { LayoutGrid, List } from 'lucide-react';

import { PendingSalesViewMode } from '../../../../types/cash/pendingCashSale.types';

import { useEmployee } from '../../../../hooks/employee/useEmployee';

import POSSearchField from '../../../POS/Fields/POSSearchField';

import POSSearchInput from '../../../POS/Fields/POSSearchInput';
import { EmployeeFilterType } from '../../../../services/employees/EmployeeService';

interface Props {
  viewMode: PendingSalesViewMode;

  onChangeView: (mode: PendingSalesViewMode) => void;

  sellerId?: number;

  onSellerChange: (id?: number) => void;

  search: string;

  onSearchChange: (value: string) => void;
}

const CashPendingSalesToolbar = ({
  viewMode,
  onChangeView,
  sellerId,
  onSellerChange,
  search,
  onSearchChange,
}: Props) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const { employeeDropdown, fetchEmployeesDropdown } = useEmployee();

  // =====================================================
  // 🔹 Load
  // =====================================================

  useEffect(() => {
    fetchEmployeesDropdown(EmployeeFilterType.PENDING_SALES);
  }, []);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div
      className="
        flex
        flex-col
        gap-3

        xl:flex-row
        xl:items-center
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Search */}
      {/* ===================================================== */}

      <div className="flex-1">
        <POSSearchInput
          value={search}
          onDebouncedChange={onSearchChange}
          placeholder="Buscar documento o cliente..."
        />
      </div>

      {/* ===================================================== */}
      {/* 🔹 Seller Filter */}
      {/* ===================================================== */}

      <div
        className="
          w-full

          xl:w-[280px]
        "
      >
        <POSSearchField
          options={employeeDropdown.map((employee) => ({
            id: employee.id,
            name: employee.name,
          }))}
          selectedValue={sellerId || null}
          onSelect={(selected) => {
            if (!selected) {
              onSellerChange(undefined);

              return;
            }

            onSellerChange(selected.id);
          }}
          placeholder="Todos los vendedores"
        />
      </div>

      {/* ===================================================== */}
      {/* 🔹 View Switch */}
      {/* ===================================================== */}

      <div
        className="
          flex
          h-[44px]
          shrink-0
          items-center
          gap-1

          self-end

          rounded-xl

          border
          border-stroke

          bg-white

          p-1

          dark:border-strokedark
          dark:bg-boxdark

          xl:self-auto
        "
      >
        {/* ===================================================== */}
        {/* 🔹 Grid */}
        {/* ===================================================== */}

        <button
          onClick={() => onChangeView('grid')}
          className={`
            flex
            h-[34px]
            w-[34px]

            items-center
            justify-center

            rounded-lg

            transition-all

            ${
              viewMode === 'grid'
                ? `
                  bg-primary
                  text-white
                `
                : `
                  text-slate-500

                  hover:text-primary

                  dark:text-slate-300
                `
            }
          `}
        >
          <LayoutGrid
            className="
              h-4
              w-4
            "
          />
        </button>

        {/* ===================================================== */}
        {/* 🔹 List */}
        {/* ===================================================== */}

        <button
          onClick={() => onChangeView('list')}
          className={`
            flex
            h-[34px]
            w-[34px]

            items-center
            justify-center

            rounded-lg

            transition-all

            ${
              viewMode === 'list'
                ? `
                  bg-primary
                  text-white
                `
                : `
                  text-slate-500

                  hover:text-primary

                  dark:text-slate-300
                `
            }
          `}
        >
          <List
            className="
              h-4
              w-4
            "
          />
        </button>
      </div>
    </div>
  );
};

export default CashPendingSalesToolbar;
